import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme
} from '@mui/material';

// Authentication imports
import { useAuth } from '../../context/AuthContext';

// Import individual dashboard components
import RoadDashboard from './RoadDashboard';
import BridgeDashboard from './BridgeDashboard';
import SlopeDashboard from './SlopeDashboard';

const DashboardPage = () => {
  const { isAuthenticated, user, hasDiscipline, getUserOrganization } = useAuth();
  const { type } = useParams(); // Get dashboard type from URL
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Redirect logic based on disciplines
  useEffect(() => {
    if (!type) {
      if (hasDiscipline('Road Pavement')) {
        navigate('/dashboard/road', { replace: true });
      } else if (hasDiscipline('Structural')) {
        navigate('/dashboard/bridge', { replace: true });
      } else if (hasDiscipline('Geotechnical')) {
        navigate('/dashboard/slope', { replace: true });
      } else {
        // Fallback for users with no dashboard disciplines
        navigate('/map-view', { replace: true });
      }
    }
  }, [type, navigate, hasDiscipline]);

  // Determine dashboard type, default to road if type is invalid but user has access
  const dashboardType = type || (hasDiscipline('Road Pavement') ? 'road' : 'bridge'); // A sensible fallback

  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationName = userOrganization?.name || '';

  // Function to render content based on dashboard type
  const renderDashboardContent = () => {
    switch (dashboardType) {
      case 'road':
        return <RoadDashboard />;
      case 'bridge':
        return <BridgeDashboard />;
      case 'slope':
        return <SlopeDashboard />;
      default:
        return <RoadDashboard />;
    }
  };

  return (
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Authentication Check */}
      {!isAuthenticated && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You must be logged in to access this page.
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {isAuthenticated && (
        <>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {dashboardType === 'road' && 'Road Dashboard'}
              {dashboardType === 'bridge' && 'Bridge Dashboard'}
              {dashboardType === 'slope' && 'Slope Dashboard'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {dashboardType === 'road' && 'Real-time overview of road asset health and defect management'}
              {dashboardType === 'bridge' && 'Comprehensive monitoring for bridge infrastructure health and maintenance'}
              {dashboardType === 'slope' && 'Advanced monitoring for slope stability and geotechnical assessment'}
            </Typography>
            {/* 🔐 ADD USER PERMISSION CONTEXT INFO */}
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {organizationName && user?.role !== 'system_admin' && (
                <>
                  <span>{organizationName}</span>
                </>
              )}
              {user?.role === 'system_admin' && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 500, color: theme.palette.warning.main }}>
                    System Admin View (All Organizations)
                  </span>
                </>
              )}
              {user?.permissionScope === 'network_section' && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 500, color: theme.palette.info.main }}>
                    Limited Access ({(user.accessible_networks?.length || 0)} networks, {(user.accessible_sections?.length || 0)} sections)
                  </span>
                </>
              )}
            </Typography>
          </Box>

          {/* Dashboard Content */}
          {renderDashboardContent()}
        </>
      )}
    </Box>
  );
};

export default DashboardPage;